import axios from 'axios';

/**
 * VindiClient - Cliente para comunicação com a API Vindi
 *
 * Implementa autenticação Basic Auth para endpoints públicos e privados da Vindi.
 * Utilizado para tokenização de cartões e gestão de assinaturas/faturas.
 */
class VindiClient {
  /**
   * @param {string} apiKey - Chave API da Vindi (pública para tokenização, privada para endpoints autenticadas)
   * @param {boolean} isSandbox - Define se está usando ambiente sandbox (default: true)
   */
  constructor(apiKey, isSandbox = true) {
    this.apiKey = apiKey;
    this.baseURL = isSandbox
      ? 'https://sandbox-app.vindi.com.br/api/v1'
      : 'https://app.vindi.com.br/api/v1';

    // Gera Basic Auth header manualmente (base64 de "apiKey:")
    const basicAuth = btoa(`${this.apiKey}:`);

    // Criar instância do axios com configurações base
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 30000, // 30 segundos de timeout
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Basic ${basicAuth}`, // Basic Auth manual
      },
    });

    // Interceptor para log de requisições em desenvolvimento
    if (process.env.NODE_ENV === 'development') {
      this.client.interceptors.request.use((config) => {
        console.log('Vindi Request:', {
          method: config.method?.toUpperCase(),
          url: config.url,
          baseURL: config.baseURL,
          headers: {
            'Content-Type': config.headers['Content-Type'],
            'Authorization': config.headers['Authorization'] ? 'Basic ***' : 'MISSING',
          },
          data: config.data,
        });
        return config;
      });

      this.client.interceptors.response.use(
        (response) => {
          console.log('Vindi Response:', {
            status: response.status,
            data: response.data,
          });
          return response;
        },
        (error) => {
          console.error('Vindi Error:', {
            status: error.response?.status,
            data: error.response?.data,
            message: error.message,
          });
          return Promise.reject(error);
        }
      );
    }
  }

  /**
   * Faz uma requisição GET
   * @param {string} url - Endpoint da API
   * @param {Object} config - Configurações adicionais do axios
   * @returns {Promise} Promise da resposta axios
   */
  async get(url, config = {}) {
    return this.client.get(url, config);
  }

  /**
   * Faz uma requisição POST
   * @param {string} url - Endpoint da API
   * @param {Object} data - Dados a serem enviados
   * @param {Object} config - Configurações adicionais do axios
   * @returns {Promise} Promise da resposta axios
   */
  async post(url, data = {}, config = {}) {
    return this.client.post(url, data, config);
  }

  /**
   * Faz uma requisição PUT
   * @param {string} url - Endpoint da API
   * @param {Object} data - Dados a serem enviados
   * @param {Object} config - Configurações adicionais do axios
   * @returns {Promise} Promise da resposta axios
   */
  async put(url, data = {}, config = {}) {
    return this.client.put(url, data, config);
  }

  /**
   * Faz uma requisição DELETE
   * @param {string} url - Endpoint da API
   * @param {Object} config - Configurações adicionais do axios
   * @returns {Promise} Promise da resposta axios
   */
  async delete(url, config = {}) {
    return this.client.delete(url, config);
  }

  /**
   * Faz uma requisição PATCH
   * @param {string} url - Endpoint da API
   * @param {Object} data - Dados a serem enviados
   * @param {Object} config - Configurações adicionais do axios
   * @returns {Promise} Promise da resposta axios
   */
  async patch(url, data = {}, config = {}) {
    return this.client.patch(url, data, config);
  }

  /**
   * Método genérico para requisições customizadas
   * @param {Object} config - Configuração completa da requisição
   * @returns {Promise} Promise da resposta axios
   */
  async request(config) {
    return this.client.request(config);
  }
}

export default VindiClient;
